import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, FormControlLabel, MenuItem, Paper, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { useAuth } from '../../providers/AuthProvider';
import { formatDateShort } from '../../misc/dateFormat';
import {
  moodleStatsService, MoodleActivity, MoodleCycle, MoodleStatsReport, MoodleStudentStats,
} from '../../services/moodleStatsService';

// Au-delà, le dernier accès au cours est signalé (et filtrable)
const INACTIVE_DAYS = 7;
const MOODLE_URL = 'https://foad.univ-rennes.fr';
// Cycles masqués : préférence de chaque enseignant·e, gardée dans son navigateur
const HIDDEN_CYCLES_KEY = 'moodleStats.hiddenCycles';

// Tri par colonne fixe, ou par moyenne d'un cycle (« average:<cycle> »)
type SortKey = 'name' | 'td' | 'last_seen' | 'active' | `average:${string}`;

const fullName = (s: MoodleStudentStats) => `${s.lastname ?? ''} ${s.firstname ?? ''}`.trim();

const daysSince = (iso: string | null): number | null =>
  iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000) : null;

const formatCollectedAt = (iso: string) => {
  // L'API renvoie l'heure UTC sans fuseau
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  return `${formatDateShort(d)} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

const moodleLink = (a: MoodleActivity) => `${MOODLE_URL}/mod/${a.module}/view.php?id=${a.cmid}`;

const loadHiddenCycles = (): string[] => {
  try {
    const value = JSON.parse(localStorage.getItem(HIDDEN_CYCLES_KEY) ?? '[]');
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

// Suivi Moodle (enseignant·es) : dernier accès, régularité, accès aux ressources et notes des activités
// suivies, collectés toutes les 12 h depuis les exports enseignant du cours Moodle. Une colonne groupée
// par cycle (préparatoire, préambule…), que chacun·e peut masquer.
const MoodleStatsPage: React.FC = () => {
  const theme = useTheme();
  const { getValidToken } = useAuth();
  const [report, setReport] = useState<MoodleStatsReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tdFilter, setTdFilter] = useState('');
  const [search, setSearch] = useState('');
  const [inactiveOnly, setInactiveOnly] = useState(false);
  const [hiddenCycles, setHiddenCycles] = useState<string[]>(loadHiddenCycles);
  const [sort, setSort] = useState<{ key: SortKey; asc: boolean }>({ key: 'name', asc: true });

  useEffect(() => {
    (async () => {
      try {
        const token = await getValidToken();
        if (token) setReport(await moodleStatsService.getReport(token));
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [getValidToken]);

  const toggleCycle = (key: string) => {
    setHiddenCycles(current => {
      const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
      try {
        localStorage.setItem(HIDDEN_CYCLES_KEY, JSON.stringify(next));
      } catch {
        // navigation privée : le choix vaut pour la visite en cours
      }
      return next;
    });
    // Tri sur la moyenne d'un cycle qu'on masque : retour au tri par nom
    setSort(s => (s.key === `average:${key}` ? { key: 'name', asc: true } : s));
  };

  const tdGroups = useMemo(
    () => [...new Set((report?.students ?? []).map(s => s.td_group).filter((td): td is string => !!td))].sort(),
    [report],
  );

  // Colonnes de chaque cycle affiché : ressources (accès), puis activités notées, puis moyenne éventuelle
  const visibleCycles = useMemo(() => (report?.cycles ?? [])
    .filter(c => !hiddenCycles.includes(c.key))
    .map(cycle => ({
      cycle,
      resources: report!.resources.filter(a => a.cycle === cycle.key),
      activities: report!.activities.filter(a => a.cycle === cycle.key),
    }))
    .filter(c => c.resources.length + c.activities.length > 0), [report, hiddenCycles]);

  const rows = useMemo(() => {
    if (!report) return [];
    const needle = search.trim().toLowerCase();
    const filtered = report.students.filter(s =>
      (!tdFilter || s.td_group === tdFilter)
      && (!needle || fullName(s).toLowerCase().includes(needle))
      && (!inactiveOnly || (daysSince(s.last_seen_at) ?? Infinity) >= INACTIVE_DAYS));
    const value = (s: MoodleStudentStats): string | number => {
      if (sort.key.startsWith('average:')) return s.averages[sort.key.slice('average:'.length)]?.average ?? -1;
      switch (sort.key) {
        case 'td': return s.td_group ?? '';
        case 'last_seen': return s.last_seen_at ?? '';
        case 'active': return s.active_days_30;
        default: return fullName(s).toLowerCase();
      }
    };
    return [...filtered].sort((a, b) => {
      const va = value(a), vb = value(b);
      const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), 'fr');
      return sort.asc ? cmp : -cmp;
    });
  }, [report, tdFilter, search, inactiveOnly, sort]);

  const gradeColor = (grade: number | null | undefined) => {
    if (grade === null || grade === undefined) return undefined;
    if (grade >= 20) return theme.palette.success.main;
    if (grade > 0) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  // Séparation verticale entre deux cycles
  const cycleStart = { borderLeft: `2px solid ${theme.palette.divider}` };

  const gradeCell = (grade: number | null | undefined, key: string, first: boolean) => {
    const color = gradeColor(grade);
    return (
      <TableCell key={key} align="center"
        sx={{ bgcolor: color ? alpha(color, 0.15) : undefined, whiteSpace: 'nowrap', ...(first ? cycleStart : {}) }}>
        {grade === null || grade === undefined ? '–' : Number(grade.toFixed(2))}
      </TableCell>
    );
  };

  const accessCell = (s: MoodleStudentStats, a: MoodleActivity, first: boolean) => {
    const view = s.views[String(a.cmid)];
    return (
      <TableCell key={a.key} align="center" sx={{
        bgcolor: alpha(view ? theme.palette.success.main : theme.palette.error.main, 0.12),
        whiteSpace: 'nowrap', ...(first ? cycleStart : {}),
      }}>
        {view
          ? <Tooltip title={`${view.count} consultation(s), du ${formatDateShort(view.first)} au ${formatDateShort(view.last)}`}>
            <span>✓ {formatDateShort(view.first)}</span>
          </Tooltip>
          : '–'}
      </TableCell>
    );
  };

  const sortable = (key: SortKey, label: string) => (
    <TableSortLabel active={sort.key === key} direction={sort.key === key && !sort.asc ? 'desc' : 'asc'}
      onClick={() => setSort(s => ({ key, asc: s.key === key ? !s.asc : key === 'name' || key === 'td' }))}>
      {label}
    </TableSortLabel>
  );

  const activityHeader = (a: MoodleActivity, first: boolean, kind: string) => (
    <TableCell key={a.key} align="center" sx={{ top: 37, whiteSpace: 'nowrap', ...(first ? cycleStart : {}) }}>
      <Tooltip title={`${kind}${a.title}${a.optional ? ' (hors moyenne si non notée)' : ''}`}>
        <a href={moodleLink(a)} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{a.label}</a>
      </Tooltip>
    </TableCell>
  );

  const averageCell = (s: MoodleStudentStats, cycle: MoodleCycle) => {
    const avg = s.averages[cycle.key];
    if (!avg) return <TableCell key={`${cycle.key}-avg`} align="center">–</TableCell>;
    return (
      <TableCell key={`${cycle.key}-avg`} align="center" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
        <Tooltip title={`${avg.done} activité(s) réussie(s) sur ${avg.counted} comptée(s)`}>
          <span>
            {avg.average.toFixed(1)}{' '}
            <Typography component="span" variant="caption" color="text.secondary">({avg.done}/{avg.counted})</Typography>
          </span>
        </Tooltip>
      </TableCell>
    );
  };

  const weeklyBars = (s: MoodleStudentStats) => (
    <Tooltip title={`Jours actifs par semaine (${report!.weeks.length} dernières semaines, de la plus ancienne à la plus récente) : ${s.weekly_active_days.join(' · ')}`}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 22 }}>
        {s.weekly_active_days.map((n, i) => (
          <Box key={i} sx={{
            width: 6, height: n ? 4 + (n / 7) * 18 : 2,
            bgcolor: n ? theme.palette.primary.main : theme.palette.divider, borderRadius: '1px',
          }} />
        ))}
      </Box>
    </Tooltip>
  );

  const lastSeenCell = (s: MoodleStudentStats) => {
    const days = daysSince(s.last_seen_at);
    const late = days === null || days >= INACTIVE_DAYS;
    const label = !s.in_moodle ? 'Pas dans Moodle' : days === null ? 'Jamais' : days === 0 ? "Aujourd'hui"
      : `${formatDateShort(s.last_seen_at!)} (${days} j)`;
    return (
      <TableCell sx={{ color: late ? theme.palette.error.main : undefined, whiteSpace: 'nowrap', fontWeight: late ? 600 : undefined }}>
        {label}
      </TableCell>
    );
  };

  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!report) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>Suivi Moodle</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {report.collected_at
          ? `Données Moodle du ${formatCollectedAt(report.collected_at)} (collecte toutes les 12 h).`
          : "Aucune collecte pour l'instant : le worker moodle-sync la fait toutes les 12 h."}
        {' '}Moyenne d'un cycle : une activité sans note compte 0, sauf l'ETAPE 6b (hors moyenne si non notée).
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', my: 2 }}>
        <TextField select size="small" label="Groupe TD" value={tdFilter} onChange={e => setTdFilter(e.target.value)}
          sx={{ minWidth: 140 }}>
          <MenuItem value="">Tous</MenuItem>
          {tdGroups.map(td => <MenuItem key={td} value={td}>{td}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Rechercher" value={search} onChange={e => setSearch(e.target.value)} />
        <FormControlLabel control={<Switch checked={inactiveOnly} onChange={e => setInactiveOnly(e.target.checked)} />}
          label={`Sans accès depuis ${INACTIVE_DAYS} j ou plus`} />
        <Typography variant="body2" color="text.secondary">{rows.length} étudiant·es</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">Cycles :</Typography>
        {report.cycles.map(c => {
          const shown = !hiddenCycles.includes(c.key);
          return (
            <Chip key={c.key} label={c.label} size="small" clickable onClick={() => toggleCycle(c.key)}
              color={shown ? 'primary' : 'default'} variant={shown ? 'filled' : 'outlined'}
              icon={shown ? <VisibilityIcon /> : <VisibilityOffIcon />} />
          );
        })}
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 280px)' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2}>{sortable('name', 'Étudiant·e')}</TableCell>
              <TableCell rowSpan={2}>{sortable('td', 'TD')}</TableCell>
              <TableCell rowSpan={2}>{sortable('last_seen', 'Dernier accès')}</TableCell>
              <TableCell rowSpan={2} align="center">
                <Tooltip title="Jours avec au moins une action dans le cours, sur 7 jours / 30 jours">
                  <span>{sortable('active', 'Jours actifs 7 j / 30 j')}</span>
                </Tooltip>
              </TableCell>
              <TableCell rowSpan={2}>Régularité</TableCell>
              {visibleCycles.map(({ cycle, resources, activities }) => (
                <TableCell key={cycle.key} align="center" sx={cycleStart}
                  colSpan={resources.length + activities.length + (cycle.average ? 1 : 0)}>
                  {cycle.label}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {visibleCycles.map(({ cycle, resources, activities }) => [
                ...resources.map((a, i) => activityHeader(a, i === 0, 'Accès à : ')),
                ...activities.map((a, i) => activityHeader(a, i === 0 && resources.length === 0, '')),
                cycle.average && (
                  <TableCell key={`${cycle.key}-avg`} align="center" sx={{ top: 37 }}>
                    {sortable(`average:${cycle.key}`, 'Moy.')}
                  </TableCell>
                ),
              ])}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(s => (
              <TableRow key={s.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fullName(s)}</TableCell>
                <TableCell>{s.td_group ?? '–'}</TableCell>
                {lastSeenCell(s)}
                <TableCell align="center">{s.active_days_7} / {s.active_days_30}</TableCell>
                <TableCell>{weeklyBars(s)}</TableCell>
                {visibleCycles.map(({ cycle, resources, activities }) => [
                  ...resources.map((a, i) => accessCell(s, a, i === 0)),
                  ...activities.map((a, i) => gradeCell(s.grades[a.key], a.key, i === 0 && resources.length === 0)),
                  cycle.average && averageCell(s, cycle),
                ])}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MoodleStatsPage;
